import React, { useCallback, useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../commonComponents/translate';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import InformationSection from './preview/InformationSection';
import HeaderSection from './preview/HeaderSection';
import OrderProp from '../shop/propTypes/orderProp';
import FooterSection from './preview/FooterSection';
import ProductInformation from './preview/components/products/ProductInformation';
import Notifications from '../../services/notifications';
import { OPPORTUNITIES_STATUS } from './components/helpers/opportunitiesListHelper';
import { compose } from '../../services/utilityFunctions';
import withCSRF from '../../commonComponents/withCSRF';
import FormModal from '../../commonComponents/modal/FormModal';
import { useJobStatus, useModal } from '../../services/customHooks';
import { PROVIDER_TYPE } from '../../commonComponents/propTypes/marketProductForm';
import PdfFormModal from '../../commonComponents/modal/PdfFormModal';
import Loader from '../../services/loader';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';

/**
 * Accepts or rejects the opportunity
 * @param t
 * @param _csrf
 * @param encodedOrg
 * @param token
 * @param status
 * @param signatureImage
 * @param newProducts
 */
const handleOpportunityStatus = async (
  t,
  _csrf,
  encodedOrg,
  token,
  status,
  signatureImage,
  newProducts,
) => {
  try {
    const url = `/api-public/market/opportunity/${encodedOrg}/${token}/${status}`;
    const params =
      status === OPPORTUNITIES_STATUS.APPROVED
        ? { signature: signatureImage, products: newProducts }
        : {};
    const config = {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Games',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    };
    const { data: response } = await axios.put(url, params, config);

    Notifications.showNotificationSuccess(
      t('success'),
      status === OPPORTUNITIES_STATUS.APPROVED
        ? t('opportunity.update.approved')
        : t('opportunity.update.rejected'),
    );
    window.location.reload();

    // eslint-disable-next-line consistent-return
    return response.data;
  } catch (err) {
    Notifications.showErrorFromRequest(err, 'opportunity.error');
    console.error(err);
    // eslint-disable-next-line consistent-return
    return [];
  }
};

function Opportunity(props) {
  const { t, _csrf, encodedOrg, token, opportunity: opportunityParam } = props;
  const { loggedUser } = useContext(LoggedUserContext);
  // Opportunity state
  const [opportunity, setOpportunity] = useState(opportunityParam);
  const { products, currency, frequency, status } = opportunity;
  const isStillPending = status === OPPORTUNITIES_STATUS.PENDING;
  const loaderRef = useRef(new Loader('#opportunityPreview'));

  // Aux state
  const [isChecked, setIsChecked] = useState(false);
  const [isLoadingApprove, setIsLoadingApprove] = useState(false);
  const [isLoadingReject, setIsLoadingReject] = useState(false);
  const [formModal, showFormModal] = useModal({
    show: false,
    data: null,
  });

  // region Handle export
  const { loading, initiateJob } = useJobStatus({
    statusEndpoint: jobId =>
      `/${
        loggedUser?._id ? 'jobs' : 'jobs-public'
      }/market/opportunity/export/pdf/${jobId}/status`,
    jobMethod: 'post',
    _csrf,
    onSuccess: downloadUrl => {
      Notifications.showNotificationSuccess(t('success'), t('success.pdf'));
      window.open(downloadUrl, '_blank');
    },
    onFailure: failedReason => {
      Notifications.showNotificationError(t('error'), failedReason);
    },
    progressMessage: progressPDF => `${t('processing')} ${progressPDF}...`,
    retryInterval: 300,
    loader: loaderRef,
  });

  const handleExportPDF = useCallback(() => {
    initiateJob(
      loggedUser?._id
        ? `/jobs/market/opportunity/${opportunityParam?._id}/export/pdf`
        : `/jobs-public/market/opportunity/${encodedOrg}/${opportunityParam?.token}/export/pdf`,
    );
  }, [initiateJob]);
  // region Handle export

  // region handle button click
  const handleFormModal = useCallback(
    (
      formProviderType,
      productId,
      variation,
      formQuestionData,
      formAnswerData,
      formAnswersLink,
    ) => {
      const isSurveyJSForm = formProviderType === PROVIDER_TYPE.SURVEY_JS;

      const formToModal = isSurveyJSForm
        ? {
            productId,
            formQuestionData,
            formAnswerData,
            providerType: variation?.form?.providerType,
          }
        : {
            productId,
            providerData: formAnswerData || null,
            productForm: variation?.form,
            providerType: variation?.form?.providerType,
            formAnswersLink,
          };
      showFormModal({
        show: true,
        data: formToModal,
      });
    },
    [showFormModal],
  );

  const handleFormSave = (productId, formAnswers, pdfLink = null) => {
    if (productId) {
      setOpportunity(prevOpportunity => {
        const { products: prevProducts } = prevOpportunity;

        const updatedProducts = prevProducts.map(product => {
          // Check if the product _id matches the specified productId
          if (product._id === productId) {
            // Replace the formAnswerData with the provided formAnswers
            return {
              ...product,
              formAnswerData: formAnswers,
              formAnswersLink: pdfLink,
            };
          }
          return product; // Return unchanged product if _id doesn't match
        });
        return {
          ...prevOpportunity,
          products: updatedProducts,
        };
      });
    }
    showFormModal({
      show: false,
      data: null,
    });
  };

  const handleCheckbox = useCallback(
    () => setIsChecked(prevState => !prevState),
    [setIsChecked],
  );

  const handleAcceptOffer = useCallback(
    async signatureImage => {
      setIsLoadingApprove(true);
      await handleOpportunityStatus(
        t,
        _csrf,
        encodedOrg,
        token,
        OPPORTUNITIES_STATUS.APPROVED,
        signatureImage,
        opportunity.products,
      );
      setIsLoadingApprove(true);
    },
    [setIsLoadingApprove, opportunity.products],
  );
  const handleRejectOffer = useCallback(async () => {
    setIsLoadingReject(true);
    await handleOpportunityStatus(
      t,
      _csrf,
      encodedOrg,
      token,
      OPPORTUNITIES_STATUS.REJECTED,
    );
    setIsLoadingReject(false);
  }, [setIsLoadingReject]);
  // endregion handle button click

  return (
    <>
      {formModal.show ? (
        <>
          {formModal?.data?.providerType === PROVIDER_TYPE.SURVEY_JS ? (
            <FormModal
              form={formModal.data}
              onClose={handleFormSave}
              onSave={handleFormSave}
              isDisabled={!isStillPending}
            />
          ) : (
            <PdfFormModal
              form={formModal.data}
              onClose={handleFormSave}
              isDisabled={!isStillPending}
              opportunity={opportunity}
            />
          )}
        </>
      ) : null}
      <Title title={t('opportunity.preview')}>
        <ContentContainer>
          <SubHeader
            title={t('opportunity.preview')}
            wrapperClass="opportunity-preview"
          />
          <div id="opportunityPreview" className="m-content shop">
            <InformationSection
              opportunity={opportunity}
              isLoading={loading}
              onExportPDF={handleExportPDF}
            />
            <HeaderSection opportunity={opportunity} />
            {products?.map((product, index) => (
              <ProductInformation
                currency={currency}
                key={product._id}
                product={product}
                index={index}
                owner={opportunity?.owner}
                onOpenForm={handleFormModal}
                frequency={frequency}
              />
            ))}
            <FooterSection
              opportunity={opportunity}
              onAcceptOffer={handleAcceptOffer}
              onRejectOffer={handleRejectOffer}
              isChecked={isChecked}
              isLoading={{ approve: isLoadingApprove, reject: isLoadingReject }}
              onCheckboxChange={handleCheckbox}
            />
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

Opportunity.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  encodedOrg: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  opportunity: PropTypes.shape(OrderProp.propType).isRequired,
};

export default compose(translate, withCSRF)(Opportunity);
